/**
 * Convert Google Places API price_level enum to integer
 * New API returns strings like "PRICE_LEVEL_MODERATE", database expects 0-4
 * @param {string|number|null} priceLevel - Price level from Google API
 * @returns {number|null} Integer 0-4 or null
 */
const convertPriceLevelToInteger = (priceLevel) => {
  // If already a number, validate and return
  if (typeof priceLevel === 'number') {
    if (priceLevel >= 0 && priceLevel <= 4) {
      return priceLevel;
    }
    return null;
  }
  
  // If null or undefined, return null
  if (!priceLevel) {
    return null;
  }
  
  // Convert string enum to integer
  const priceLevelMap = {
    'PRICE_LEVEL_FREE': 0,
    'PRICE_LEVEL_INEXPENSIVE': 1,
    'PRICE_LEVEL_MODERATE': 2,
    'PRICE_LEVEL_EXPENSIVE': 3,
    'PRICE_LEVEL_VERY_EXPENSIVE': 4
  };
  
  const upperPriceLevel = String(priceLevel).toUpperCase();
  
  if (priceLevelMap.hasOwnProperty(upperPriceLevel)) {
    return priceLevelMap[upperPriceLevel];
  }
  
  // Unknown format, return null
  console.warn(`⚠️ Unknown price_level format: "${priceLevel}", setting to null`);
  return null;
};

/**
 * Transform Google Places opening hours to our JSONB format
 * Handles both opening_hours and current_opening_hours
 * 
 * @param {Object} hoursObject - Google Places hours object
 * @returns {Object} Structured hours object for JSONB storage
 */
export function transformOpeningHours(hoursObject) {
  if (!hoursObject) return null;

  try {
    return {
      open_now: hoursObject.open_now || false,
      
      // Periods define when the place is open
      // Each period has open and close times with day (0-6, Sunday=0) and time (HHMM format)
      periods: hoursObject.periods?.map(period => ({
        open: period.open ? {
          day: period.open.day,  // 0-6, Sunday = 0
          time: period.open.time,  // Format: "0900" for 9:00 AM
          hours: period.open.hours,  // Sometimes provided
          minutes: period.open.minutes  // Sometimes provided
        } : null,
        close: period.close ? {
          day: period.close.day,
          time: period.close.time,
          hours: period.close.hours,
          minutes: period.close.minutes
        } : null
      })) || [],
      
      // Human-readable text for each day
      // Example: ["Monday: 9:00 AM – 5:00 PM", "Tuesday: 9:00 AM – 5:00 PM", ...]
      weekday_text: hoursObject.weekday_text || [],
      
      // Additional fields for current_opening_hours
      secondary_hours_type: hoursObject.secondary_hours_type || null,  // For seasonal hours
      special_days: hoursObject.special_days || null  // Holiday hours
    };
  } catch (error) {
    console.error('Error transforming opening hours:', error);
    return null;
  }
}

/**
 * Transform Google Places photo array to our JSONB format
 * 
 * @param {Array} photos - Array of photo objects from Google Places
 * @param {number} maxPhotos - Maximum number of photos to include
 * @returns {Array} Transformed photo array for JSONB storage
 */
export function transformPhotos(photos, maxPhotos = 10) {
  if (!photos || photos.length === 0) return null;

  try {
    return photos.slice(0, maxPhotos).map(photo => {
      // Handle both old REST API format and new Places API v1 format
      const photoReference = photo.photo_reference || photo.name?.split('/').pop() || null;
      const width = photo.width || photo.widthPx || null;
      const height = photo.height || photo.heightPx || null;
      
      return {
        // Photo reference used to construct image URL
        photo_reference: photoReference,
        
        // Photo name (Places API v1 format)
        name: photo.name || null,
        
        // Dimensions
        width: width,
        height: height,
        
        // Attributions (required by Google's terms)
        html_attributions: photo.html_attributions || [],
        
        // Construct full photo URL for convenience
        // Format: https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference={reference}&key={key}
        url_template: photoReference 
          ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth={width}&photoreference=${photoReference}&key={API_KEY}`
          : null,
        
        // Store original metadata
        raw: photo
      };
    });
  } catch (error) {
    console.error('Error transforming photos:', error);
    return null;
  }
}

/**
 * Normalize review object to consistent format (saved format)
 * Handles both display format (author, authorPhoto, relativeTime, publishTime)
 * and saved format (author_name, profile_photo_url, relative_time_description, time)
 * 
 * @param {Object} review - Review object in any format
 * @returns {Object} Normalized review in saved format
 */
export function normalizeReview(review) {
  if (!review) return null;
  
  // Handle both display format and saved format
  const authorName = review.author_name || review.author || review.authorAttribution?.displayName || 'Anonymous';
  const authorUrl = review.author_url || review.authorAttribution?.uri || null;
  const profilePhotoUrl = review.profile_photo_url || review.authorPhoto || review.authorAttribution?.photoUri || null;
  const reviewText = review.text?.text || review.text || '';
  const relativeTime = review.relative_time_description || review.relativeTime || review.relativePublishTimeDescription || null;
  const publishTime = review.time || review.publishTime || null;
  
  return {
    // Author information
    author_name: authorName,
    author_url: authorUrl,
    profile_photo_url: profilePhotoUrl,
    
    // Rating and content
    rating: review.rating || null,  // 1-5
    text: reviewText,
    
    // Timing
    time: publishTime,  // Unix timestamp or ISO string
    relative_time_description: relativeTime,  // "a month ago"
    
    // Language and translation (if available)
    language: review.language || 'en',
    original_language: review.original_language || null,
    translated: review.translated || false,
    
    // Store original for reference (if not already normalized)
    raw: review.raw || review
  };
}

/**
 * Transform Google Places review array to our JSONB format
 * Sanitizes and structures review data
 * 
 * @param {Array} reviews - Array of review objects from Google Places
 * @param {number} maxReviews - Maximum number of reviews to include
 * @returns {Array} Transformed review array for JSONB storage
 */
export function transformReviews(reviews, maxReviews = 5) {
  if (!reviews || reviews.length === 0) return null;

  try {
    return reviews.slice(0, maxReviews).map(review => {
      // Handle both old REST API format and new Places API v1 format
      const authorName = review.author_name || review.authorAttribution?.displayName || 'Anonymous';
      const authorUrl = review.author_url || review.authorAttribution?.uri || null;
      const profilePhotoUrl = review.profile_photo_url || review.authorAttribution?.photoUri || null;
      const reviewText = review.text || review.text?.text || '';
      const relativeTime = review.relative_time_description || review.relativePublishTimeDescription || null;
      const publishTime = review.time || review.publishTime || null;
      
      return {
        // Author information
        author_name: authorName,
        author_url: authorUrl,
        profile_photo_url: profilePhotoUrl,
        
        // Rating and content
        rating: review.rating,  // 1-5
        text: reviewText,
        
        // Timing
        time: publishTime,  // Unix timestamp or ISO string
        relative_time_description: relativeTime,  // "a month ago"
        
        // Language and translation
        language: review.language || 'en',
        original_language: review.original_language || null,
        translated: review.translated || false,
        
        // Store original for reference
        raw: review
      };
    });
  } catch (error) {
    console.error('Error transforming reviews:', error);
    return null;
  }
}

/**
 * Determine which field tier was used based on what data is present
 * This helps track what level of enrichment a camp has received
 * 
 * @param {Object} googlePlace - Google Places API response
 * @returns {string} Field tier: 'minimal' | 'search' | 'essential' | 'enhanced' | 'maximum'
 */
export function determineFieldTier(googlePlace) {
  // Check for atmosphere data (most expensive tier)
  const hasAtmosphereData = 
    googlePlace.opening_hours || 
    googlePlace.current_opening_hours || 
    googlePlace.reviews?.length > 0 ||
    googlePlace.photos?.length > 0;
  
  // Check for contact data
  const hasContactData = 
    googlePlace.formatted_phone_number || 
    googlePlace.international_phone_number ||
    googlePlace.website ||
    googlePlace.websiteUri;
  
  // Check for basic extended data
  const hasExtendedBasic = 
    googlePlace.plus_code ||
    googlePlace.url ||
    googlePlace.googleMapsUri ||
    googlePlace.vicinity;
  
  // Determine tier
  if (hasAtmosphereData && hasContactData) {
    return 'maximum';
  } else if (hasContactData && hasExtendedBasic) {
    return 'enhanced';
  } else if (hasContactData) {
    return 'essential';
  } else if (hasExtendedBasic) {
    return 'search';
  } else {
    return 'minimal';
  }
}

/**
 * Transform Google Places API response to camp_audit_data database format
 * Handles both search results and details API responses
 * 
 * @param {Object} googlePlace - Raw Google Places API response
 * @param {Object} options - Transform options
 * @param {boolean} options.includeReviews - Include full review objects (default: true)
 * @param {boolean} options.includePhotos - Include photo references (default: true)
 * @param {number} options.maxReviews - Maximum reviews to include (default: 5)
 * @param {number} options.maxPhotos - Maximum photos to include (default: 10)
 * @returns {Object} Transformed camp data ready for database insert/update
 */
export const transformGooglePlaceForDB = (googlePlace, options = {}) => {
  const {
    includeReviews = true,
    includePhotos = true,
    maxReviews = 5,
    maxPhotos = 10
  } = options;

  console.group('🔄 Transforming Google Place for DB');
  console.log('📥 Input Google Place:', googlePlace);
  
  try {
    // ============================================================================
    // COORDINATES - Handle multiple format variations
    // ============================================================================
    
    // Google returns coordinates in different formats:
    // - Search results: geometry.location.lat/lng as functions
    // - Details API: geometry.location.lat/lng as values
    // - Some cached: location.lat/lng directly
    // - Places API v1: location.latitude/longitude
    
    const lat = 
      googlePlace.geometry?.location?.lat?.() ||  // Function call (search results)
      googlePlace.geometry?.location?.lat ||      // Direct value (details)
      googlePlace.location?.lat ||                 // Cached format
      googlePlace.location?.latitude ||            // Places API v1 format
      null;
      
    const lng = 
      googlePlace.geometry?.location?.lng?.() ||  // Function call (search results)
      googlePlace.geometry?.location?.lng ||      // Direct value (details)
      googlePlace.location?.lng ||                 // Cached format
      googlePlace.location?.longitude ||           // Places API v1 format
      null;
    
    // ✅ DETERMINE IF MISSING (but don't reject)
    const missingCoordinates = lat === null || lat === undefined || lng === null || lng === undefined;
    
    if (missingCoordinates) {
      console.log('⚠️ Camp has no coordinates, will store with validation flag:', googlePlace.name);
    }
    
    // ============================================================================
    // PLUS CODES - Extract global and local codes
    // ============================================================================
    
    const plusCode = googlePlace.plus_code || {};
    const plusCodeGlobal = plusCode.global_code || plusCode.globalCode || null;
    const plusCodeLocal = plusCode.compound_code || plusCode.compoundCode || null;

    // ============================================================================
    // OPENING HOURS - Transform to structured JSONB
    // ============================================================================
    
    const openingHours = googlePlace.opening_hours || googlePlace.regularOpeningHours 
      ? transformOpeningHours(googlePlace.opening_hours || googlePlace.regularOpeningHours) 
      : null;
    const currentOpeningHours = googlePlace.current_opening_hours || googlePlace.currentOpeningHours 
      ? transformOpeningHours(googlePlace.current_opening_hours || googlePlace.currentOpeningHours) 
      : null;

    // ============================================================================
    // PHOTOS - Transform photo references with metadata
    // ============================================================================
    
    const photos = includePhotos && googlePlace.photos 
      ? transformPhotos(googlePlace.photos, maxPhotos)
      : null;

    // ============================================================================
    // REVIEWS - Transform review objects with sanitization
    // ============================================================================
    
    const reviews = includeReviews && googlePlace.reviews 
      ? transformReviews(googlePlace.reviews, maxReviews)
      : null;

    // ============================================================================
    // CATEGORIZATION - Extract and categorize place types
    // ============================================================================
    
    const primaryCategory = categorizePrimaryType(googlePlace.types || [], googlePlace.name || '');
    const categorization = categorizeProgramType(googlePlace);

    // ============================================================================
    // EDITORIAL SUMMARY - Handle both string and object formats
    // ============================================================================
    
    const editorialSummary = 
      typeof googlePlace.editorial_summary === 'string'
        ? googlePlace.editorial_summary
        : googlePlace.editorial_summary?.overview || 
          googlePlace.editorial_summary?.description ||
          googlePlace.editorialSummary?.text ||
          null;

    // ============================================================================
    // UTC OFFSET - Handle both formats
    // ============================================================================
    
    const utcOffset = 
      googlePlace.utc_offset_minutes ||  // New format
      googlePlace.utcOffset ||           // Legacy format
      null;

    // ============================================================================
    // ACCESSIBILITY - Extract wheelchair accessibility
    // ============================================================================
    
    const wheelchairAccessible = 
      googlePlace.wheelchair_accessible_entrance || 
      googlePlace.wheelchairAccessibleEntrance || 
      null;

    // ============================================================================
    // CONTACT INFORMATION - Handle multiple formats
    // ============================================================================
    
    const phone = googlePlace.formatted_phone_number || googlePlace.nationalPhoneNumber || null;
    const internationalPhone = googlePlace.international_phone_number || googlePlace.internationalPhoneNumber || null;
    const website = googlePlace.website || googlePlace.websiteUri || null;
    const adrAddress = googlePlace.adr_address || googlePlace.addressComponents || null;

    // ============================================================================
    // GOOGLE MAPS URL - Handle multiple formats
    // ============================================================================
    
    const googleMapsUrl = 
      googlePlace.url || 
      googlePlace.googleMapsUri || 
      null;

    // ============================================================================
    // ICON URL - Handle multiple formats
    // ============================================================================
    
    const iconUrl = 
      googlePlace.icon || 
      googlePlace.iconUri || 
      null;

    // ============================================================================
    // BUILD COMPLETE TRANSFORMED OBJECT
    // ============================================================================
    
    const transformed = {
      // -------------------------------------------------------------------------
      // Core Identity (Basic Data)
      // -------------------------------------------------------------------------
      place_id: googlePlace.place_id || googlePlace.id || null,
      name: googlePlace.name || googlePlace.displayName?.text || null,
      location: googlePlace.formatted_address || googlePlace.vicinity || googlePlace.formattedAddress || '',
      formatted_address: googlePlace.formatted_address || googlePlace.formattedAddress || null,
      
      // -------------------------------------------------------------------------
      // Coordinates (Basic Data)
      // -------------------------------------------------------------------------
      coordinates_lat: lat ? Number(lat) : null,
      coordinates_lng: lng ? Number(lng) : null,
      
      // ✅ VALIDATION FLAGS
      needs_validation: missingCoordinates,
      validation_reason: missingCoordinates ? 'missing_coordinates' : null,
      validation_status: missingCoordinates ? 'needs_coordinates' : 'complete',
      
      // -------------------------------------------------------------------------
      // Contact Information (Contact Data tier)
      // -------------------------------------------------------------------------
      phone: phone,
      international_phone_number: internationalPhone,
      website: website,
      adr_address: adrAddress,  // Microformat structured address
      
      // -------------------------------------------------------------------------
      // Location Enhancement (Basic Data)
      // -------------------------------------------------------------------------
      google_maps_url: googleMapsUrl,  // Direct Google Maps link
      vicinity: googlePlace.vicinity || googlePlace.shortFormattedAddress || googlePlace.formatted_address || null,  // Readable short address
      icon_url: iconUrl,  // Google's icon for this place type
      plus_code_global: plusCodeGlobal,  // Global plus code
      plus_code_local: plusCodeLocal,  // Local plus code with locality
      utc_offset_minutes: utcOffset,  // UTC offset in minutes
      
      // -------------------------------------------------------------------------
      // Accessibility & Attributes (Basic Data)
      // -------------------------------------------------------------------------
      wheelchair_accessible: wheelchairAccessible,
      
      // Note: good_for_children and good_for_groups are not provided by Google Places API
      // These would need to be inferred from reviews or set by community contributions
      
      // -------------------------------------------------------------------------
      // Operating Hours (Atmosphere Data tier)
      // -------------------------------------------------------------------------
      opening_hours: openingHours ? JSON.stringify(openingHours) : null,
      current_opening_hours: currentOpeningHours ? JSON.stringify(currentOpeningHours) : null,
      
      // -------------------------------------------------------------------------
      // Ratings & Reviews (Atmosphere Data tier)
      // -------------------------------------------------------------------------
      rating: googlePlace.rating || null,
      user_ratings_total: googlePlace.user_ratings_total || googlePlace.userRatingCount || 0,
      price_level: convertPriceLevelToInteger(googlePlace.price_level || googlePlace.priceLevel),
      business_status: googlePlace.business_status || googlePlace.businessStatus || 'OPERATIONAL',
      
      // -------------------------------------------------------------------------
      // Media (Atmosphere Data tier)
      // -------------------------------------------------------------------------
      photos: photos ? JSON.stringify(photos) : null,
      photo_count: photos?.length || (Array.isArray(googlePlace.photos) ? googlePlace.photos.length : 0),
      reviews: reviews ? JSON.stringify(reviews) : null,
      editorial_summary: editorialSummary,
      
      // Note: review_summary is a different field - we store Google's editorial in editorial_summary
      
      // -------------------------------------------------------------------------
      // Google Categorization (Basic Data)
      // -------------------------------------------------------------------------
      google_types: Array.isArray(googlePlace.types) ? googlePlace.types : [],
      
      // -------------------------------------------------------------------------
      // Our Categorization (derived from Google types)
      // -------------------------------------------------------------------------
      camp_type: determineCampType(googlePlace) || primaryCategory, // Day Camp / Overnight Camp, fallback to activity category
      primary_category: categorization.primary_category || primaryCategory,
      all_categories: categorization.all_categories || Array.isArray(googlePlace.types) ? googlePlace.types : [],
      category_confidence: categorization.confidence || 'medium',
      keywords_matched: categorization.keywords_matched || [],
      
      // -------------------------------------------------------------------------
      // Activities (derived)
      // -------------------------------------------------------------------------
      activities: extractActivities(googlePlace),
      
      // -------------------------------------------------------------------------
      // Search Optimization
      // -------------------------------------------------------------------------
      popularity_score: calculateInitialPopularity(googlePlace),
      search_keywords: generateSearchKeywords(googlePlace),
      
      // -------------------------------------------------------------------------
      // Metadata & Tracking
      // -------------------------------------------------------------------------
      last_fetched_from_google: new Date().toISOString(),
      last_details_fetch: new Date().toISOString(),
      details_fetch_tier: determineFieldTier(googlePlace),
      data_freshness_days: 0,
      needs_refresh: false,
      
      // -------------------------------------------------------------------------
      // Initialize engagement metrics
      // -------------------------------------------------------------------------
      view_count: 0,
      save_count: 0,
      click_count: 0,
      
      // -------------------------------------------------------------------------
      // Audit Fields
      // -------------------------------------------------------------------------
      audit_id: `CAMP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audit_date: new Date().toISOString(),
      audit_year: new Date().getFullYear(),
      audit_week: getWeekNumber(new Date()),
      api_compliant: true,
      ethical_source: true,
      source: 'google_places_api'
    };
    
    console.log('📤 Output for DB:', transformed);
    
    // Validate the transformed data
    const validation = validatePlaceData(transformed);
    console.log('✅ Validation result:', validation);
    
    if (!validation.isValid) {
      console.error('❌ Validation failed:', validation.errors);
      throw new Error(`Invalid place data: ${validation.errors.join(', ')}`);
    }
    
    // ✅ ADD THIS: Log warnings but don't throw
    if (validation.warnings && validation.warnings.length > 0) {
      console.log('⚠️ Validation warnings:', validation.warnings);
      // Don't throw - just log warnings
    }
    
    console.log('✅ Transform complete:', {
      name: transformed.name,
      hasCoordinates: !missingCoordinates,
      needsValidation: transformed.needs_validation,
      tier: transformed.details_fetch_tier
    });
    
    console.groupEnd();
    return transformed;
    
  } catch (error) {
    console.error('❌ Transformation error:', error);
    console.groupEnd();
    throw error;
  }
};

/**
 * Determine if camp is Day Camp or Overnight Camp
 * Analyzes name, editorial summary, and reviews for keywords
 * 
 * @param {Object} googlePlace - Google Places API response
 * @returns {string|null} 'Day Camp', 'Overnight Camp', or null if cannot determine
 */
export function determineCampType(googlePlace) {
  const name = (googlePlace.name || googlePlace.displayName?.text || '').toLowerCase();
  const editorialSummary = typeof googlePlace.editorial_summary === 'string'
    ? googlePlace.editorial_summary
    : googlePlace.editorial_summary?.overview || 
      googlePlace.editorialSummary?.text || '';
  
  // Combine all searchable text
  const searchText = `${name} ${editorialSummary}`.toLowerCase();
  
  // Day Camp keywords (higher priority - check first)
  const dayCampKeywords = [
    'day camp', 'daycare', 'day-care', 'after school', 'afterschool',
    'drop-off', 'pick-up', 'daily', 'commuter camp', 'no overnight',
    'day program', 'daytime camp'
  ];
  
  // Overnight Camp keywords
  const overnightCampKeywords = [
    'overnight camp', 'sleepaway camp', 'sleep-away', 'resident camp',
    'residential camp', 'boarding camp', 'sleep over', 'sleepover',
    'week-long', 'week long', 'multi-day', 'multi day', 'stays overnight',
    'overnight program', 'residential program'
  ];
  
  // Check for day camp indicators
  const hasDayCampKeywords = dayCampKeywords.some(keyword => 
    searchText.includes(keyword)
  );
  
  // Check for overnight camp indicators
  const hasOvernightKeywords = overnightCampKeywords.some(keyword => 
    searchText.includes(keyword)
  );
  
  // Priority: explicit keywords override generic "camp"
  if (hasOvernightKeywords) {
    return 'Overnight Camp';
  }
  
  if (hasDayCampKeywords) {
    return 'Day Camp';
  }
  
  // If name contains just "camp" without modifiers, check reviews for clues
  if (name.includes('camp') && googlePlace.reviews) {
    const reviewText = googlePlace.reviews
      .slice(0, 3) // Check top 3 reviews
      .map(r => {
        const text = typeof r.text === 'string' 
          ? r.text 
          : r.text?.text || '';
        return text.toLowerCase();
      })
      .join(' ');
    
    if (overnightCampKeywords.some(k => reviewText.includes(k))) {
      return 'Overnight Camp';
    }
    if (dayCampKeywords.some(k => reviewText.includes(k))) {
      return 'Day Camp';
    }
  }
  
  // Default: if it's clearly a camp but we can't determine, return null
  // (You might want to default to 'Day Camp' if most camps in your area are day camps)
  return null; // or 'Day Camp' as default
}

/**
 * Categorize place based on Google types with name-aware STEM detection
 * IMPORTANT: Check name for STEM keywords BEFORE type-based mapping
 * This ensures camps like "Code Ninjas" are categorized as STEM, not generic "education"
 * @param {Array<string>} types - Google Places types array
 * @param {string} name - Place name for keyword detection
 * @returns {string} Category: 'stem', 'education', 'sports', 'arts', 'outdoor', 'community', 'general'
 */
export const categorizePrimaryType = (types = [], name = '') => {
  if (!types || types.length === 0) return 'general';
  
  // Normalize name and types for matching
  const nameLower = (name || '').toLowerCase();
  const normalizedTypes = types.map(t => String(t).toLowerCase());
  
  // PRIORITY 1: STEM Keywords in Name (before type-based mapping!)
  const stemKeywords = [
    'code', 'coding', 'coder', 'programmer', 'programming', 'ninjas',
    'robot', 'robotics', 'stem', 'steam', 
    'tech', 'technology', 'computer', 'software',
    'engineering', 'engineer', 'science', 'math', 'mathematics',
    'python', 'javascript', 'java', 'web dev', 'web development',
    'app dev', 'app development', 'game dev', 'game development',
    'ai', 'artificial intelligence', 'machine learning', 'ml'
  ];
  
  const hasStemInName = stemKeywords.some(keyword => 
    nameLower.includes(keyword)
  );
  
  // If name suggests STEM and type is generic (school, point_of_interest, etc)
  // then categorize as STEM, not the generic type
  if (hasStemInName) {
    const genericTypes = ['school', 'point_of_interest', 'establishment', 
                         'child_care_agency', 'education'];
    const onlyHasGenericTypes = normalizedTypes.every(t => 
      genericTypes.includes(t)
    );
    
    if (onlyHasGenericTypes || normalizedTypes.includes('school')) {
      return 'stem'; // STEM category takes priority over generic "education"
    }
  }
  
  // PRIORITY 2: Specific STEM types (aquarium, zoo, etc)
  if (normalizedTypes.includes('aquarium') || 
      normalizedTypes.includes('zoo') || 
      normalizedTypes.includes('planetarium')) {
    return 'stem';
  }
  
  // PRIORITY 3: Type-based mapping (original logic)
  const categoryMap = {
    'school': 'education',
    'university': 'education',
    'campground': 'outdoor',
    'park': 'outdoor',
    'gym': 'sports',
    'sports_complex': 'sports',
    'museum': 'arts',
    'art_gallery': 'arts',
    'community_center': 'community',
    'aquarium': 'stem',
    'zoo': 'stem',
    'library': 'education',
    'recreation_center': 'sports',
    'swimming_pool': 'sports'
  };
  
  // Find first matching type
  for (const type of normalizedTypes) {
    if (categoryMap[type]) {
      return categoryMap[type];
    }
  }
  
  return 'general';
};

/**
 * Categorize program based on name, types, and editorial summary
 * This categorization will be saved to database when user favorites/saves
 */
const categorizeProgramType = (googlePlace) => {
  const name = (googlePlace.name || googlePlace.displayName?.text || '').toLowerCase();
  const types = googlePlace.types || [];
  const editorialSummary = typeof googlePlace.editorial_summary === 'string'
    ? googlePlace.editorial_summary
    : googlePlace.editorial_summary?.overview || googlePlace.editorialSummary?.text || '';
  const searchText = `${name} ${editorialSummary}`.toLowerCase();
  
  // Detect if it's a youth-focused program
  const youthKeywords = [
    'children', 'kids', 'youth', 'junior', 'teen', 'toddler',
    'camp', 'summer camp', 'day camp', 'after school'
  ];
  
  const isYouthProgram = youthKeywords.some(keyword => 
    searchText.includes(keyword)
  );
  
  // Category detection with priority order
  const categories = [];
  
  // 1. Summer Camps (highest priority for our app)
  if (searchText.includes('camp') || 
      searchText.includes('summer camp') || 
      searchText.includes('day camp') ||
      searchText.includes('overnight camp') ||
      searchText.includes('sleepaway camp') ||
      types.includes('campground')) {
    categories.push('Summer Camp');
  }
  
  // 2. Sports & Recreation
  if (searchText.match(/sport|athletic|swim|soccer|basketball|baseball|tennis|football|volleyball|martial arts|gymnastics/) ||
      types.some(t => ['gym', 'sports_club', 'sports_complex', 'stadium', 'swimming_pool'].includes(t))) {
    categories.push('Sports & Recreation');
  }
  
  // 3. STEM & Education
  if (searchText.match(/stem|science|coding|robot|tech|computer|engineering|math/) ||
      types.some(t => ['school', 'university', 'library'].includes(t))) {
    categories.push('STEM & Education');
  }
  
  // 4. Arts & Culture
  if (searchText.match(/art|music|dance|theater|drama|painting|drawing|craft|pottery/) ||
      types.some(t => ['art_gallery', 'museum', 'performing_arts_theater'].includes(t))) {
    categories.push('Arts & Culture');
  }
  
  // 5. Outdoor & Adventure
  if (searchText.match(/outdoor|nature|hiking|adventure|climbing|kayak|canoe/) ||
      types.some(t => ['park', 'national_park', 'campground'].includes(t))) {
    categories.push('Outdoor & Adventure');
  }
  
  // 6. Community Programs
  if (searchText.match(/ymca|boys girls club|community center|recreation center/) ||
      types.includes('community_center')) {
    categories.push('Community Programs');
  }
  
  // 7. Enrichment
  if (searchText.match(/enrichment|tutoring|learning|education|language/) ||
      types.includes('child_care_agency')) {
    categories.push('Enrichment');
  }
  
  return {
    primary_category: categories[0] || 'General Youth Program',
    all_categories: categories,
    is_youth_program: isYouthProgram,
    confidence: categories.length > 0 ? 'high' : 'medium',
    keywords_matched: youthKeywords.filter(k => searchText.includes(k))
  };
};

/**
 * Extract activities from place data
 */
const extractActivities = (googlePlace) => {
  const activities = new Set();
  
  // Extract from types
  const activityTypes = ['sports', 'outdoor', 'education', 'arts', 'swimming', 'recreation'];
  (googlePlace.types || []).forEach(type => {
    activityTypes.forEach(activity => {
      if (type.includes(activity)) {
        activities.add(activity);
      }
    });
  });
  
  // Extract from name
  const name = (googlePlace.name || googlePlace.displayName?.text || '').toLowerCase();
  if (name.includes('camp')) activities.add('camping');
  if (name.includes('sport')) activities.add('sports');
  if (name.includes('art')) activities.add('arts');
  if (name.includes('science')) activities.add('STEM');
  if (name.includes('music')) activities.add('music');
  if (name.includes('swim')) activities.add('swimming');
  if (name.includes('gym')) activities.add('fitness');
  
  return Array.from(activities).join(', ') || 'general activities';
};

/**
 * Calculate initial popularity score from Google Places data
 * Updated to use all available Atmosphere tier data
 * 
 * @param {Object} googlePlace - Google Places API response
 * @returns {number} Popularity score (0-100)
 */
export function calculateInitialPopularity(googlePlace) {
  let score = 0;

  // Rating contribution (0-50 points)
  const rating = googlePlace.rating || 0;
  const reviewCount = googlePlace.user_ratings_total || googlePlace.userRatingCount || 0;
  
  if (rating && reviewCount) {
    const ratingScore = (rating / 5) * 30;  // 0-30 points for rating
    const reviewCountScore = Math.min(reviewCount / 10, 20);  // 0-20 points for review count
    score += ratingScore + reviewCountScore;
  }

  // Price level contribution (0-10 points, lower price = higher score for camps)
  const priceLevel = googlePlace.price_level || googlePlace.priceLevel;
  if (priceLevel !== null && priceLevel !== undefined) {
    const priceLevelInt = typeof priceLevel === 'number' ? priceLevel : 
      (priceLevel === 'PRICE_LEVEL_FREE' ? 0 :
       priceLevel === 'PRICE_LEVEL_INEXPENSIVE' ? 1 :
       priceLevel === 'PRICE_LEVEL_MODERATE' ? 2 :
       priceLevel === 'PRICE_LEVEL_EXPENSIVE' ? 3 :
       priceLevel === 'PRICE_LEVEL_VERY_EXPENSIVE' ? 4 : null);
    
    if (priceLevelInt !== null) {
      score += (4 - priceLevelInt) * 2.5;  // Inverted: cheaper = more popular for camps
    }
  }

  // Photo contribution (0-10 points)
  if (googlePlace.photos?.length) {
    score += Math.min(googlePlace.photos.length, 10);
  }

  // Business status contribution (0-10 points)
  const businessStatus = googlePlace.business_status || googlePlace.businessStatus;
  if (businessStatus === 'OPERATIONAL') {
    score += 10;
  }

  // Has contact info bonus (0-10 points)
  const hasPhone = googlePlace.formatted_phone_number || googlePlace.nationalPhoneNumber;
  const hasWebsite = googlePlace.website || googlePlace.websiteUri;
  if (hasPhone || hasWebsite) {
    score += 5;
  }
  if (hasPhone && hasWebsite) {
    score += 5;  // Bonus for having both
  }

  // Has opening hours bonus (0-10 points)
  if (googlePlace.opening_hours || googlePlace.current_opening_hours || 
      googlePlace.regularOpeningHours || googlePlace.currentOpeningHours) {
    score += 10;
  }

  return Math.min(Math.round(score), 100);  // Cap at 100
}

/**
 * Generate search keywords from all available Google Places data
 * Updated to extract keywords from new Maximum tier fields
 * 
 * @param {Object} googlePlace - Google Places API response
 * @returns {Array<string>} Array of search keywords
 */
export function generateSearchKeywords(googlePlace) {
  const keywords = new Set();
  
  // Name tokenization
  const name = googlePlace.name || googlePlace.displayName?.text || '';
  if (name) {
    name.toLowerCase().split(/\s+/).forEach(word => {
      if (word.length > 2) keywords.add(word);
    });
  }

  // Address tokenization
  const formattedAddress = googlePlace.formatted_address || googlePlace.formattedAddress || '';
  if (formattedAddress) {
    formattedAddress.toLowerCase().split(/[,\s]+/).forEach(word => {
      if (word.length > 2) keywords.add(word);
    });
  }

  // Vicinity (shorter address)
  const vicinity = googlePlace.vicinity || googlePlace.shortFormattedAddress || '';
  if (vicinity) {
    vicinity.toLowerCase().split(/[,\s]+/).forEach(word => {
      if (word.length > 2) keywords.add(word);
    });
  }

  // Types (e.g., "summer_camp", "day_care")
  if (googlePlace.types) {
    googlePlace.types.forEach(type => {
      type.split('_').forEach(word => keywords.add(word));
    });
  }

  // Editorial summary
  const editorialSummary = typeof googlePlace.editorial_summary === 'string'
    ? googlePlace.editorial_summary
    : googlePlace.editorial_summary?.overview || googlePlace.editorialSummary?.text || '';
  
  if (editorialSummary) {
    editorialSummary.toLowerCase().split(/\s+/).forEach(word => {
      if (word.length > 3) keywords.add(word);
    });
  }

  // Reviews (top keywords from review text)
  if (googlePlace.reviews && Array.isArray(googlePlace.reviews)) {
    googlePlace.reviews.slice(0, 3).forEach(review => {
      // Handle both string and object formats (Places API v1 uses structured text)
      let reviewText = '';
      if (typeof review.text === 'string') {
        reviewText = review.text;
      } else if (review.text && typeof review.text === 'object' && review.text.text) {
        reviewText = review.text.text;
      } else if (review.text) {
        reviewText = String(review.text);
      }
      
      if (reviewText && typeof reviewText === 'string') {
        const words = reviewText.toLowerCase()
          .match(/\b[a-z]{4,}\b/g) || [];  // Words 4+ chars
        words.slice(0, 10).forEach(word => keywords.add(word));
      }
    });
  }

  // Plus code locality
  const plusCode = googlePlace.plus_code || {};
  if (plusCode.compound_code || plusCode.compoundCode) {
    const compoundCode = plusCode.compound_code || plusCode.compoundCode;
    const locality = compoundCode.split(' ').slice(1).join(' ');
    locality.split(/\s+/).forEach(word => {
    if (word.length > 2) keywords.add(word.toLowerCase());
  });
  }

  // Category keywords (from our categorization)
  const categorization = categorizeProgramType(googlePlace);
  if (categorization.keywords_matched) {
    categorization.keywords_matched.forEach(keyword => keywords.add(keyword.toLowerCase()));
  }
  if (categorization.all_categories) {
    categorization.all_categories.forEach(cat => {
      cat.toLowerCase().split(/\s+/).forEach(word => {
        if (word.length > 2) keywords.add(word);
      });
    });
  }

  return Array.from(keywords).filter(k => k.length > 2);
}

/**
 * Get ISO week number for a date
 * 
 * @param {Date} date - Date to get week number for
 * @returns {number} Week number (1-53)
 */
export function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Validate transformed place data before database insert
 * Now more permissive - allows camps without coordinates
 */
export const validatePlaceData = (data) => {
  const errors = [];
  const warnings = [];
  
  // REQUIRED fields (must have)
  if (!data.place_id) errors.push('Missing place_id');
  if (!data.name) errors.push('Missing name');
  
  // PREFERRED fields (should have but not fatal)
  if (data.coordinates_lat === null || data.coordinates_lat === undefined ||
      data.coordinates_lng === null || data.coordinates_lng === undefined) {
    warnings.push('Missing coordinates');
  }
  
  if (!data.formatted_address && !data.location) {
    warnings.push('Missing address');
  }
  
  if (!data.phone && !data.website) {
    warnings.push('Missing contact information');
  }
  
  // Type validation
  if (data.google_types && !Array.isArray(data.google_types)) {
    errors.push('google_types must be an array');
  }
  
  if (data.all_categories && !Array.isArray(data.all_categories)) {
    errors.push('all_categories must be an array');
  }
  
  if (data.search_keywords && !Array.isArray(data.search_keywords)) {
    errors.push('search_keywords must be an array');
  }
  
  if (data.photos && typeof data.photos !== 'string') {
    errors.push('photos must be a JSON string');
  }
  
  if (data.reviews && typeof data.reviews !== 'string') {
    errors.push('reviews must be a JSON string');
  }
  
  // Range validation
  if (data.rating && (data.rating < 0 || data.rating > 5)) {
    errors.push('rating must be between 0 and 5');
  }
  
  if (data.price_level && (data.price_level < 0 || data.price_level > 4)) {
    errors.push('price_level must be between 0 and 4');
  }
  
  // Coordinate validation
  if (data.coordinates_lat && (data.coordinates_lat < -90 || data.coordinates_lat > 90)) {
    errors.push('coordinates_lat must be between -90 and 90');
  }
  
  if (data.coordinates_lng && (data.coordinates_lng < -180 || data.coordinates_lng > 180)) {
    errors.push('coordinates_lng must be between -180 and 180');
  }
  
  return {
    isValid: errors.length === 0, // Valid if no hard errors
    errors,
    warnings,
    needsValidation: warnings.length > 0,
    validationReason: warnings.length > 0 
      ? warnings.join('; ') 
      : null
  };
};

/**
 * Test function with sample data
 */
export const testTransformation = () => {
  const sampleGooglePlace = {
    place_id: "ChIJTest123",
    name: "Test Summer Camp",
    formatted_address: "123 Test St, Atlanta, GA 30301",
    geometry: {
      location: {
        lat: 33.7490,
        lng: -84.3880
      }
    },
    rating: 4.5,
    user_ratings_total: 127,
    types: ["campground", "point_of_interest"],
    photos: [{ photo_reference: "test123" }],
    business_status: "OPERATIONAL"
  };
  
  console.log('🧪 Testing transformation with sample data...');
  const result = transformGooglePlaceForDB(sampleGooglePlace);
  console.log('✅ Test result:', result);
  return result;
};

/**
 * Test STEM categorization fix
 * Verifies that camps with STEM keywords in names are categorized correctly
 */
/**
 * Determine if a Google Place is a camp or youth program that should be saved to database
 * Excludes addresses, cities, and generic locations that are not camps
 * 
 * @param {Object} googlePlace - Google Places API response or place object
 * @returns {boolean} - true if should be saved as camp, false if should be skipped
 */
export function isCampOrYouthProgram(googlePlace) {
  if (!googlePlace) {
    return false;
  }

  const name = (googlePlace.name || googlePlace.displayName?.text || '').toLowerCase();
  const types = googlePlace.types || [];
  const description = (googlePlace.description || googlePlace.editorialSummary?.text || '').toLowerCase();
  const formattedAddress = (googlePlace.formatted_address || googlePlace.formattedAddress || '').toLowerCase();

  // ============================================================================
  // EXCLUSION CRITERIA - These should NOT be saved
  // ============================================================================

  // 1. Address types - exclude street addresses, routes, premises
  const addressTypes = [
    'street_address',
    'route',
    'premise',
    'subpremise',
    'street_number',
    'intersection',
    'political'
  ];
  
  if (types.some(type => addressTypes.includes(type))) {
    // Exception: If it's a premise but has camp keywords, it might be a camp location
    if (types.includes('premise') || types.includes('subpremise')) {
      const hasCampKeywords = name.includes('camp') || 
                              name.includes('ymca') || 
                              name.includes('boys girls club') ||
                              name.includes('boys & girls club');
      if (!hasCampKeywords) {
        return false;
      }
    } else {
      return false;
    }
  }

  // 2. Geographic/political boundaries - exclude cities, states, zip codes
  const geographicTypes = [
    'locality',
    'administrative_area_level_1', // state
    'administrative_area_level_2', // county
    'administrative_area_level_3',
    'administrative_area_level_4',
    'administrative_area_level_5',
    'postal_code',
    'country',
    'neighborhood',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2'
  ];
  
  if (types.some(type => geographicTypes.includes(type))) {
    return false;
  }

  // 3. Generic venues without camp indicators
  // Schools without camp keywords
  if (types.includes('school') || types.includes('primary_school') || 
      types.includes('secondary_school') || types.includes('university')) {
    const hasCampKeywords = name.includes('camp') || 
                            name.includes('summer') ||
                            description.includes('camp') ||
                            description.includes('summer program');
    if (!hasCampKeywords) {
      return false;
    }
  }

  // Stadiums, parking lots, etc. without camp indicators
  const genericVenueTypes = [
    'parking',
    'parking_lot',
    'stadium', // Unless it's a camp stadium
    'bus_station',
    'train_station',
    'subway_station',
    'transit_station',
    'gas_station',
    'restaurant',
    'store',
    'shopping_mall'
  ];
  
  if (types.some(type => genericVenueTypes.includes(type))) {
    // Exception: If name/description has camp keywords, it might be relevant
    const hasCampKeywords = name.includes('camp') || 
                            description.includes('camp') ||
                            description.includes('youth program');
    if (!hasCampKeywords) {
      return false;
    }
  }

  // 4. If name is just an address (starts with number and street name pattern)
  const addressPattern = /^\d+\s+[a-z\s]+(?:street|st|avenue|ave|road|rd|drive|dr|boulevard|blvd|lane|ln|way|circle|cir|court|ct|place|pl)/i;
  if (addressPattern.test(name) && !name.includes('camp') && !description.includes('camp')) {
    return false;
  }

  // ============================================================================
  // INCLUSION CRITERIA - These SHOULD be saved
  // ============================================================================

  // 1. Explicit camp keywords in name or description
  const campKeywords = [
    'camp',
    'summer camp',
    'day camp',
    'overnight camp',
    'sleepaway camp',
    'resident camp',
    'ymca',
    'ywca',
    'boys girls club',
    'boys & girls club',
    'jcc',
    'jewish community center',
    'youth program',
    'youth center',
    'children\'s program',
    'kids program'
  ];
  
  const hasCampKeyword = campKeywords.some(keyword => 
    name.includes(keyword) || description.includes(keyword)
  );
  
  if (hasCampKeyword) {
    return true;
  }

  // 2. Camp-related Google Places types
  const campTypes = [
    'campground', // Might be a camp
    'rv_park' // Sometimes used for camps
  ];
  
  if (types.some(type => campTypes.includes(type))) {
    // Additional check: if it's a campground, verify it's not just a camping site
    // by checking if it has youth/camp keywords
    if (types.includes('campground') && !hasCampKeyword) {
      // If it's just a campground without camp keywords, it's probably not a youth camp
      return false;
    }
    return true;
  }

  // 3. Community centers with youth program indicators
  if (types.includes('community_center') || types.includes('recreation_center')) {
    const hasYouthIndicators = name.includes('youth') ||
                               name.includes('children') ||
                               name.includes('kids') ||
                               description.includes('youth') ||
                               description.includes('children') ||
                               description.includes('kids') ||
                               description.includes('camp') ||
                               hasCampKeyword;
    if (hasYouthIndicators) {
      return true;
    }
  }

  // 4. Sports/Arts/Education venues that might offer youth programs
  // But only if they have youth/camp indicators
  const youthProgramTypes = [
    'gym',
    'sports_complex',
    'sports_club',
    'museum',
    'art_gallery',
    'performing_arts_theater'
  ];
  
  if (types.some(type => youthProgramTypes.includes(type))) {
    const hasYouthIndicators = name.includes('youth') ||
                               name.includes('children') ||
                               name.includes('kids') ||
                               description.includes('youth program') ||
                               description.includes('children') ||
                               description.includes('kids program') ||
                               description.includes('summer program');
    if (hasYouthIndicators) {
      return true;
    }
  }

  // 5. Parks with educational/youth program indicators
  if (types.includes('park') || types.includes('national_park')) {
    const hasYouthIndicators = name.includes('nature center') ||
                               name.includes('environmental') ||
                               description.includes('youth program') ||
                               description.includes('education') ||
                               description.includes('children') ||
                               description.includes('camp');
    if (hasYouthIndicators) {
      return true;
    }
    // Otherwise, it's just a park, not a camp
    return false;
  }

  // Default: If we can't determine it's a camp/youth program, don't save it
  return false;
}

export const testSTEMCategorization = () => {
  console.log('🧪 Testing STEM categorization fix...');
  
  const testCases = [
    {
      name: 'Code Ninjas - The Woodlands North/Magnolia',
      types: ['child_care_agency', 'school', 'point_of_interest', 'establishment'],
      expected: 'stem',
      description: 'Code Ninjas should be STEM (has "code" keyword)'
    },
    {
      name: 'First Robotics Academy',
      types: ['school', 'point_of_interest'],
      expected: 'stem',
      description: 'Robotics should be STEM (has "robot" keyword)'
    },
    {
      name: 'Tech Academy for Kids',
      types: ['school'],
      expected: 'stem',
      description: 'Tech Academy should be STEM (has "tech" keyword)'
    },
    {
      name: 'Lincoln Elementary School',
      types: ['school'],
      expected: 'education',
      description: 'Regular school without STEM keywords should be education'
    },
    {
      name: 'Elite Soccer Academy',
      types: ['school'],
      expected: 'education',
      description: 'Soccer academy without STEM keywords should be education'
    }
  ];
  
  let passed = 0;
  let failed = 0;
  
  testCases.forEach((testCase, index) => {
    // Access the function directly (it's exported but not as const)
    const result = categorizePrimaryType(testCase.types, testCase.name);
    const success = result === testCase.expected;
    
    if (success) {
      passed++;
      console.log(`✅ Test ${index + 1}: ${testCase.description}`);
      console.log(`   Result: "${result}" (expected: "${testCase.expected}")`);
    } else {
      failed++;
      console.error(`❌ Test ${index + 1} FAILED: ${testCase.description}`);
      console.error(`   Result: "${result}" (expected: "${testCase.expected}")`);
      console.error(`   Name: "${testCase.name}"`);
      console.error(`   Types: ${JSON.stringify(testCase.types)}`);
    }
  });
  
  console.log(`\n📊 Test Results: ${passed} passed, ${failed} failed`);
  return { passed, failed, total: testCases.length };
};
